import { useEffect, useRef, useState } from 'react'
import env from '@/config/env'
import { getApiHeaders } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'

export type StreamingInsightsState = {
  /** Insight prose accumulated so far. May be partial during streaming. */
  insight: string
  /** Suggestion prose accumulated so far. May be partial during streaming. */
  suggestion: string
  /** null while the meta line hasn't arrived yet. */
  hasEnoughData: boolean | null
  /** True when the response came from the in-memory cache (single chunk after
   *  metadata). Frontend skips the typewriter animation in this case so cache
   *  hits feel instant. null until metadata arrives. */
  cached: boolean | null
  isLoading: boolean
  error: Error | null
  empty?: { totalPosts: number; minPosts: number }
}

const INITIAL: StreamingInsightsState = {
  insight: '',
  suggestion: '',
  hasEnoughData: null,
  cached: null,
  isLoading: false,
  error: null,
}

/** AI insight is only meaningful for "all platforms" or a single platform. */
export function isInsightScope(platforms: PlatformType[]) {
  return platforms.length <= 1
}

/**
 * Fetch the analytics-insight stream and progressively parse INSIGHT/SUGGESTION
 * sections out of the response body. Both halves expose accumulated text as it
 * arrives, so React renders create a typewriter effect for free.
 *
 * Cache hits arrive as a single chunk after the metadata line — the same
 * parsing path handles both cases.
 */
export function useStreamingInsights(platforms: PlatformType[]): StreamingInsightsState {
  const [state, setState] = useState<StreamingInsightsState>(INITIAL)

  // Stable string key for the effect dep — sorted+joined so {ig,li} === {li,ig}.
  const platformKey = [...platforms].sort().join(',')
  const inScope = isInsightScope(platforms)

  // Keep latest fetch's abort controller so a filter change cancels the old
  // stream rather than racing two updates against the same state.
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!inScope) {
      abortRef.current?.abort()
      setState(INITIAL)
      return
    }

    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller

    setState({ ...INITIAL, isLoading: true })

    void runStream({
      platforms,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      signal: controller.signal,
      onState: (next) => {
        if (controller.signal.aborted) return
        setState((prev) => ({ ...prev, ...next }))
      },
    })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformKey, inScope])

  return state
}

async function runStream(opts: {
  platforms: PlatformType[]
  timezone: string
  signal: AbortSignal
  onState: (patch: Partial<StreamingInsightsState>) => void
}) {
  const { platforms, timezone, signal, onState } = opts

  try {
    const res = await fetch(`${env.VITE_BACKEND_URL}/stream/analytics-insight`, {
      method: 'POST',
      headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platforms: platforms.length > 0 ? platforms : undefined,
        timezone,
      }),
      signal,
    })

    if (!res.ok || !res.body) {
      throw new Error(`Insight stream failed: ${res.status}`)
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let metaParsed = false

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // First line of the response is JSON metadata; everything after the
      // first newline is the body text we parse for INSIGHT/SUGGESTION.
      if (!metaParsed) {
        const newlineIdx = buffer.indexOf('\n')
        if (newlineIdx === -1) continue
        const metaLine = buffer.slice(0, newlineIdx)
        const meta = JSON.parse(metaLine) as
          | { hasEnoughData: true; cached: boolean }
          | { hasEnoughData: false; totalPosts: number; minPosts: number }
        metaParsed = true

        if (!meta.hasEnoughData) {
          onState({
            hasEnoughData: false,
            cached: false,
            isLoading: false,
            empty: { totalPosts: meta.totalPosts, minPosts: meta.minPosts },
          })
          return
        }
        onState({ hasEnoughData: true, cached: meta.cached })
      }

      const newlineIdx = buffer.indexOf('\n')
      const body = newlineIdx === -1 ? '' : buffer.slice(newlineIdx + 1)
      const parsed = parseInsightText(body)
      onState({ insight: parsed.insight, suggestion: parsed.suggestion })
    }

    onState({ isLoading: false })
  } catch (err) {
    if ((err as Error).name === 'AbortError') return
    onState({ isLoading: false, error: err as Error })
  }
}

/** Split LLM output into insight / suggestion sections. The model is prompted
 *  to emit `INSIGHT:\n...\n\nSUGGESTION:\n...`; this just slices around those
 *  markers and trims the headers off. Tolerant of partial output: if SUGGESTION
 *  hasn't arrived yet, suggestion stays empty and insight contains everything
 *  after INSIGHT:. */
function parseInsightText(text: string): { insight: string; suggestion: string } {
  const insightIdx = text.indexOf('INSIGHT:')
  const suggestionIdx = text.indexOf('SUGGESTION:')

  if (insightIdx === -1) return { insight: '', suggestion: '' }

  const insightStart = insightIdx + 'INSIGHT:'.length
  if (suggestionIdx === -1) {
    return { insight: text.slice(insightStart).trim(), suggestion: '' }
  }
  return {
    insight: text.slice(insightStart, suggestionIdx).trim(),
    suggestion: text.slice(suggestionIdx + 'SUGGESTION:'.length).trim(),
  }
}
